import React, { type JSX } from "react";
import type { AdminProjectionFreshness } from "../../contracts/admin-read-models";
import { pickAdminDashboardLocaleText } from "../../shared/locale/admin-dashboard-copy";
import { AdminLocalizedLink } from "../../shared/locale/admin-dashboard-localized-link";
import { useAdminDashboardLocale } from "../../shared/locale/admin-dashboard-locale-context";
import { AdminDetailPanel } from "../../shared/ui/admin-detail-panel";
import { AdminEmptyState } from "../../shared/ui/admin-empty-state";
import { AdminOperatorExplanationBlock } from "../../shared/ui/admin-operator-explanation-block";
import { AdminSourceStatusGrid } from "../../shared/ui/admin-source-status-grid";
import { AdminStatusBadge } from "../../shared/ui/admin-status-badge";
import type {
  FailedFlowDetailViewModel,
  FailedFlowIncidentRouteActionViewModel,
  FailedFlowTraceKeyViewModel,
} from "./failed-flows-view-model";

export type FailedFlowDetailPanelProps = {
  detail?: FailedFlowDetailViewModel;
  sourceFreshness: Array<{
    source: string;
    freshness: AdminProjectionFreshness;
  }>;
};

function renderLinkOrFallback(label: string | undefined, href: string | undefined): JSX.Element | string {
  if (label === undefined || href === undefined) {
    return "-";
  }

  return <AdminLocalizedLink to={href}>{label}</AdminLocalizedLink>;
}

function renderExternalLinkOrFallback(label: string, href: string | undefined): JSX.Element | string {
  if (href === undefined) {
    return "-";
  }

  return (
    <a href={href} rel="noreferrer" target="_blank">
      {label}
    </a>
  );
}

function formatLatestTraceValue(requestId: string | undefined, correlationId: string | undefined): string {
  return `${requestId ?? "-"} / ${correlationId ?? "-"}`;
}

function renderIncidentRouteAction(
  action: FailedFlowIncidentRouteActionViewModel,
): JSX.Element {
  return (
    <div style={{ display: "grid", gap: "6px" }}>
      <span>{action.summary}</span>
      {action.href && action.linkLabel ? (
        action.href.startsWith("http") ? (
          <a href={action.href} rel="noreferrer" target="_blank">
            {action.linkLabel}
          </a>
        ) : (
          <AdminLocalizedLink to={action.href}>{action.linkLabel}</AdminLocalizedLink>
        )
      ) : null}
    </div>
  );
}

function renderTraceKey(traceKey: FailedFlowTraceKeyViewModel): JSX.Element {
  const content = traceKey.href ? (
    traceKey.href.startsWith("http") ? (
      <a href={traceKey.href} rel="noreferrer" target="_blank">
        {traceKey.value}
      </a>
    ) : (
      <AdminLocalizedLink to={traceKey.href}>{traceKey.value}</AdminLocalizedLink>
    )
  ) : (
    <span>{traceKey.value}</span>
  );

  return (
    <span
      key={traceKey.id}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        padding: "6px 10px",
        borderRadius: "999px",
        background: "rgba(148, 163, 184, 0.12)",
        border: "1px solid rgba(148, 163, 184, 0.25)",
        fontSize: "0.82rem",
        fontWeight: 600,
        flexWrap: "wrap",
      }}
    >
      <span style={{ color: "#64748b", fontWeight: 700 }}>{traceKey.label}</span>
      {content}
    </span>
  );
}

export function FailedFlowDetailPanel(props: FailedFlowDetailPanelProps): JSX.Element {
  const { locale } = useAdminDashboardLocale();
  const requestLabel = pickAdminDashboardLocaleText(locale, { en: "Request", ko: "요청" });
  const correlationLabel = pickAdminDashboardLocaleText(locale, { en: "Correlation", ko: "상관관계" });
  const copy = {
    emptyTitle: pickAdminDashboardLocaleText(locale, { en: "No failed flow selected", ko: "선택된 오류 흐름 없음" }),
    emptyBody: pickAdminDashboardLocaleText(locale, { en: "Select a table row to inspect request, correlation, and user drill-down.", ko: "표 행을 선택하면 요청, 상관관계, 사용자 추가 조사를 볼 수 있습니다." }),
    title: pickAdminDashboardLocaleText(locale, { en: "Failed flow detail", ko: "오류 흐름 상세" }),
    incidentRoute: pickAdminDashboardLocaleText(locale, { en: "Incident route", ko: "사고 조사 경로" }),
    headline: pickAdminDashboardLocaleText(locale, { en: "Headline", ko: "사고 제목" }),
    requestSummary: pickAdminDashboardLocaleText(locale, { en: "Request summary", ko: "요청 요약" }),
    traceKeys: pickAdminDashboardLocaleText(locale, { en: "Trace keys", ko: "추적 키" }),
    mediaContext: pickAdminDashboardLocaleText(locale, { en: "Linked media context", ko: "연결된 미디어 문맥" }),
    sameDeviceLogContext: pickAdminDashboardLocaleText(locale, { en: "Same-device log context", ko: "동일 기기 로그 문맥" }),
    sourceScenario: pickAdminDashboardLocaleText(locale, { en: "Source scenario", ko: "소스 시나리오" }),
    artifactIdentity: pickAdminDashboardLocaleText(locale, { en: "Artifact", ko: "아티팩트" }),
    linkedEntity: pickAdminDashboardLocaleText(locale, { en: "Linked entity", ko: "연결 엔터티" }),
    mediaKind: pickAdminDashboardLocaleText(locale, { en: "Media kind", ko: "미디어 종류" }),
    inventoryState: pickAdminDashboardLocaleText(locale, { en: "Inventory state", ko: "인벤토리 상태" }),
    missingReason: pickAdminDashboardLocaleText(locale, { en: "Missing reason", ko: "부재 이유" }),
    nextAction: pickAdminDashboardLocaleText(locale, { en: "Next action", ko: "다음 액션" }),
    relationType: pickAdminDashboardLocaleText(locale, { en: "Relation", ko: "연결 방식" }),
    artifactRole: pickAdminDashboardLocaleText(locale, { en: "Role", ko: "역할" }),
    previewAvailability: pickAdminDashboardLocaleText(locale, { en: "Preview", ko: "미리보기" }),
    downloadAvailability: pickAdminDashboardLocaleText(locale, { en: "Download", ko: "다운로드" }),
    fallbackReason: pickAdminDashboardLocaleText(locale, { en: "Fallback", ko: "Fallback" }),
    binaryReviewPath: pickAdminDashboardLocaleText(locale, { en: "Binary review path", ko: "바이너리 검토 경로" }),
    captureTrigger: pickAdminDashboardLocaleText(locale, { en: "Capture trigger", ko: "수집 트리거" }),
    previewReadiness: pickAdminDashboardLocaleText(locale, { en: "Preview readiness", ko: "미리보기 준비 상태" }),
    downloadReadiness: pickAdminDashboardLocaleText(locale, { en: "Download readiness", ko: "다운로드 준비 상태" }),
    bundleHashPresence: pickAdminDashboardLocaleText(locale, { en: "Bundle hash", ko: "Bundle hash" }),
    manifestVersion: pickAdminDashboardLocaleText(locale, { en: "Manifest", ko: "Manifest" }),
    deliveryPath: pickAdminDashboardLocaleText(locale, { en: "Delivery path", ko: "전달 경로" }),
    previewHeadline: pickAdminDashboardLocaleText(locale, { en: "Preview headline", ko: "미리보기 headline" }),
    latestTrace: pickAdminDashboardLocaleText(locale, { en: "Latest trace", ko: "최신 추적" }),
    traceLink: pickAdminDashboardLocaleText(locale, { en: "Trace link", ko: "추적 링크" }),
    firstCheck: pickAdminDashboardLocaleText(locale, { en: "First check", ko: "1차 확인" }),
    requestLog: pickAdminDashboardLocaleText(locale, { en: "Request log", ko: "요청 로그" }),
    flow: pickAdminDashboardLocaleText(locale, { en: "Flow", ko: "흐름" }),
    created: pickAdminDashboardLocaleText(locale, { en: "Created", ko: "생성 시각" }),
    status: pickAdminDashboardLocaleText(locale, { en: "Status", ko: "상태" }),
    error: pickAdminDashboardLocaleText(locale, { en: "Error", ko: "오류" }),
    httpStatus: pickAdminDashboardLocaleText(locale, { en: "HTTP status", ko: "응답 코드" }),
    signal: pickAdminDashboardLocaleText(locale, { en: "Signal", ko: "시그널" }),
    stage: pickAdminDashboardLocaleText(locale, { en: "Stage", ko: "단계" }),
    platform: pickAdminDashboardLocaleText(locale, { en: "Platform", ko: "플랫폼" }),
    runtimeAppBuildNumber: pickAdminDashboardLocaleText(locale, { en: "App build", ko: "앱 빌드" }),
    runtimeSummary: pickAdminDashboardLocaleText(locale, { en: "Runtime summary", ko: "런타임 요약" }),
    runtimeLocale: pickAdminDashboardLocaleText(locale, { en: "Runtime locale", ko: "런타임 언어" }),
    runtimeTimezone: pickAdminDashboardLocaleText(locale, { en: "Runtime timezone", ko: "런타임 시간대" }),
    runtimeTimezoneRegion: pickAdminDashboardLocaleText(locale, { en: "Timezone region", ko: "시간대 권역" }),
    runtimeDeviceModel: pickAdminDashboardLocaleText(locale, { en: "Device class", ko: "기기 분류" }),
    runtimeDeviceModelName: pickAdminDashboardLocaleText(locale, { en: "Device family", ko: "기기 계열" }),
    runtimeOsMajor: pickAdminDashboardLocaleText(locale, { en: "OS bucket", ko: "OS 버킷" }),
    runtimeOsVersionMinor: pickAdminDashboardLocaleText(locale, { en: "OS minor bucket", ko: "OS 세부 버킷" }),
    runtimeNetworkType: pickAdminDashboardLocaleText(locale, { en: "Network", ko: "네트워크" }),
    runtimePhysicalDevice: pickAdminDashboardLocaleText(locale, { en: "Device runtime", ko: "기기 런타임" }),
    providerKind: pickAdminDashboardLocaleText(locale, { en: "Auth provider", ko: "인증 provider" }),
    reauthMethod: pickAdminDashboardLocaleText(locale, { en: "Reauth method", ko: "재인증 방식" }),
    recentAuthAge: pickAdminDashboardLocaleText(locale, { en: "Recent auth age", ko: "최근 인증 경과" }),
    saveBeforeExit: pickAdminDashboardLocaleText(locale, { en: "Saved before exit", ko: "이탈 전 저장" }),
    suggestionUsed: pickAdminDashboardLocaleText(locale, { en: "Suggestion used", ko: "제안 반영 여부" }),
    profileCompletionStep: pickAdminDashboardLocaleText(locale, { en: "Profile step", ko: "프로필 단계" }),
    attemptCountBucket: pickAdminDashboardLocaleText(locale, { en: "Retry count", ko: "재시도 횟수" }),
    photoConfirmLatency: pickAdminDashboardLocaleText(locale, { en: "Photo confirm latency", ko: "사진 확인 지연" }),
    requestDraftSource: pickAdminDashboardLocaleText(locale, { en: "Draft source", ko: "초안 입력 경로" }),
    requestMediaAttachCount: pickAdminDashboardLocaleText(locale, { en: "Attached media", ko: "첨부 미디어 수" }),
    requestSpecies: pickAdminDashboardLocaleText(locale, { en: "Pet species", ko: "펫 종" }),
    requestBreedSelection: pickAdminDashboardLocaleText(locale, { en: "Breed entry", ko: "품종 입력 방식" }),
    requestTargetUserLookupKind: pickAdminDashboardLocaleText(locale, { en: "Lookup target", ko: "조회 대상" }),
    requestPublicPetCount: pickAdminDashboardLocaleText(locale, { en: "Public pets", ko: "공개 반려동물 수" }),
    requestProfileMutationFieldSet: pickAdminDashboardLocaleText(locale, {
      en: "Profile mutation fields",
      ko: "프로필 수정 필드",
    }),
    requestRoomKind: pickAdminDashboardLocaleText(locale, { en: "Room kind", ko: "대화방 유형" }),
    requestMessageLength: pickAdminDashboardLocaleText(locale, { en: "Message length", ko: "메시지 길이" }),
    requestMembershipCount: pickAdminDashboardLocaleText(locale, { en: "Membership count", ko: "구성원 수" }),
    requestScheduleRecurrence: pickAdminDashboardLocaleText(locale, { en: "Schedule recurrence", ko: "일정 반복 주기" }),
    requestDosePresence: pickAdminDashboardLocaleText(locale, { en: "Dose state", ko: "용량 상태" }),
    requestOccurrenceActionKind: pickAdminDashboardLocaleText(locale, { en: "Occurrence action", ko: "복약 액션" }),
    requestSnoozeWindow: pickAdminDashboardLocaleText(locale, { en: "Snooze window", ko: "미루기 구간" }),
    requestRideVisitLeadTime: pickAdminDashboardLocaleText(locale, { en: "Visit lead time", ko: "방문 리드타임" }),
    requestRideLongestAddressLength: pickAdminDashboardLocaleText(locale, { en: "Longest address", ko: "최장 주소 길이" }),
    requestRideMemoProvided: pickAdminDashboardLocaleText(locale, { en: "Memo provided", ko: "메모 포함 여부" }),
    requestCommunityUploadSource: pickAdminDashboardLocaleText(locale, { en: "Upload source", ko: "업로드 경로" }),
    requestCommunityContentLength: pickAdminDashboardLocaleText(locale, { en: "Post length", ko: "게시글 길이" }),
    requestCommunityCommentLength: pickAdminDashboardLocaleText(locale, { en: "Comment length", ko: "댓글 길이" }),
    requestCommunityImageCount: pickAdminDashboardLocaleText(locale, { en: "Post images", ko: "게시글 이미지 수" }),
    requestCommunityPetContextAttached: pickAdminDashboardLocaleText(locale, { en: "Pet context", ko: "펫 context" }),
    requestCommunityTopicTag: pickAdminDashboardLocaleText(locale, { en: "Topic tag", ko: "주제 태그" }),
    requestCommunityRetryCount: pickAdminDashboardLocaleText(locale, { en: "Community retries", ko: "커뮤니티 재시도" }),
    requestReceiptExtractSource: pickAdminDashboardLocaleText(locale, { en: "Receipt source", ko: "영수증 소스" }),
    requestReceiptImageByteSize: pickAdminDashboardLocaleText(locale, { en: "Receipt image size", ko: "영수증 이미지 크기" }),
    requestReceiptExtractionConfidence: pickAdminDashboardLocaleText(locale, { en: "AI extraction confidence", ko: "AI 추출 신뢰도" }),
    requestReceiptManualOverride: pickAdminDashboardLocaleText(locale, { en: "Manual override", ko: "수동 보정" }),
    requestReceiptCategoryMutationType: pickAdminDashboardLocaleText(locale, { en: "Category mutation", ko: "카테고리 변경" }),
    requestHealthFileSize: pickAdminDashboardLocaleText(locale, { en: "Health file size", ko: "건강기록 파일 크기" }),
    requestHealthFileType: pickAdminDashboardLocaleText(locale, { en: "Health file type", ko: "건강기록 파일 형식" }),
    requestHealthAnalysisFallbackPath: pickAdminDashboardLocaleText(locale, {
      en: "Analysis fallback path",
      ko: "분석 fallback 경로",
    }),
    requestHealthConfirmToSaveLatency: pickAdminDashboardLocaleText(locale, {
      en: "Confirm to save latency",
      ko: "확인 후 저장 지연",
    }),
    release: pickAdminDashboardLocaleText(locale, { en: "Release", ko: "릴리스" }),
    app: pickAdminDashboardLocaleText(locale, { en: "App", ko: "앱" }),
    device: pickAdminDashboardLocaleText(locale, { en: "Device", ko: "기기" }),
    job: pickAdminDashboardLocaleText(locale, { en: "Job", ko: "작업" }),
    user: pickAdminDashboardLocaleText(locale, { en: "User", ko: "사용자" }),
    userCue: pickAdminDashboardLocaleText(locale, { en: "User cue", ko: "사용자 단서" }),
    providerCue: pickAdminDashboardLocaleText(locale, { en: "Provider cue", ko: "로그인 공급자 단서" }),
    linkedProviders: pickAdminDashboardLocaleText(locale, { en: "Linked providers", ko: "연결된 공급자 수" }),
    pet: pickAdminDashboardLocaleText(locale, { en: "Pet", ko: "펫" }),
    record: pickAdminDashboardLocaleText(locale, { en: "Record", ko: "기록" }),
    receipt: pickAdminDashboardLocaleText(locale, { en: "Receipt", ko: "영수증" }),
    file: pickAdminDashboardLocaleText(locale, { en: "File", ko: "파일" }),
    lastEvent: pickAdminDashboardLocaleText(locale, { en: "Last event", ko: "마지막 이벤트" }),
    event: pickAdminDashboardLocaleText(locale, { en: "Event", ko: "이벤트" }),
    occurred: pickAdminDashboardLocaleText(locale, { en: "Occurred", ko: "발생 시각" }),
    drilldown: pickAdminDashboardLocaleText(locale, { en: "Drill-down", ko: "연결 조사" }),
    deviceErrors: pickAdminDashboardLocaleText(locale, { en: "Device errors", ko: "기기 오류" }),
    deviceKpis: pickAdminDashboardLocaleText(locale, { en: "Device KPI", ko: "실기기 지표" }),
    similarFailures: pickAdminDashboardLocaleText(locale, { en: "Similar failures", ko: "유사 오류" }),
    openSimilar: pickAdminDashboardLocaleText(locale, { en: "Open similar failures", ko: "유사 오류 열기" }),
    freshness: pickAdminDashboardLocaleText(locale, { en: "Freshness", ko: "신선도" }),
  };
  if (props.detail === undefined) {
    return (
      <AdminEmptyState
        title={copy.emptyTitle}
        body={copy.emptyBody}
      />
    );
  }

  return (
    <AdminDetailPanel
      title={copy.title}
      headerAction={<AdminStatusBadge value={props.detail.status} label={props.detail.statusLabel} />}
      sections={[
        {
          title: copy.incidentRoute,
          items: [
            {
              key: "headline",
              label: copy.headline,
              value: props.detail.incidentRoute.headline,
            },
            {
              key: "requestSummary",
              label: copy.requestSummary,
              value: (
                <div style={{ display: "grid", gap: "8px" }}>
                  <span>{props.detail.requestEvidence.summary}</span>
                  <AdminOperatorExplanationBlock
                    screen="failed_flows"
                    slot="failed_flows_detail_request_evidence"
                  />
                </div>
              ),
            },
            {
              key: "traceKeys",
              label: copy.traceKeys,
              value:
                props.detail.requestEvidence.traceKeys.length > 0 ? (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                    {props.detail.requestEvidence.traceKeys.map((traceKey) =>
                      renderTraceKey(traceKey),
                    )}
                  </div>
                ) : (
                  "-"
                ),
            },
            {
              key: "firstCheck",
              label: copy.firstCheck,
              value: props.detail.incidentRoute.firstCheckLabel,
            },
            {
              key: "requestLogAvailability",
              label: copy.requestLog,
              value: props.detail.requestEvidence.logAvailabilitySummary,
            },
            ...props.detail.incidentRoute.actions.map((action) => ({
              key: action.id,
              label: action.label,
              value: renderIncidentRouteAction(action),
            })),
          ],
        },
        ...(props.detail.mediaContext
          ? [
              {
                title: copy.mediaContext,
                items: [
                  {
                    key: "mediaHeadline",
                    label: copy.headline,
                    value: props.detail.mediaContext.headline,
                  },
                  {
                    key: "sourceScenario",
                    label: copy.sourceScenario,
                    value: props.detail.mediaContext.sourceScenarioLabel,
                  },
                  {
                    key: "artifactIdentity",
                    label: copy.artifactIdentity,
                    value: props.detail.mediaContext.artifactIdentityLabel,
                  },
                  {
                    key: "linkedEntity",
                    label: copy.linkedEntity,
                    value: props.detail.mediaContext.linkedEntityLabel,
                  },
                  {
                    key: "mediaKind",
                    label: copy.mediaKind,
                    value: props.detail.mediaContext.mediaKindLabel,
                  },
                  ...(props.detail.mediaContext.inventoryStateLabel
                    ? [
                        {
                          key: "inventoryState",
                          label: copy.inventoryState,
                          value: props.detail.mediaContext.inventoryStateLabel,
                        },
                      ]
                    : []),
                  ...(props.detail.mediaContext.missingReasonLabel
                    ? [
                        {
                          key: "missingReason",
                          label: copy.missingReason,
                          value: props.detail.mediaContext.missingReasonLabel,
                        },
                      ]
                    : []),
                  ...(props.detail.mediaContext.nextActionLabel
                    ? [
                        {
                          key: "nextAction",
                          label: copy.nextAction,
                          value: props.detail.mediaContext.nextActionLabel,
                        },
                      ]
                    : []),
                  ...(props.detail.mediaContext.relationTypeLabel
                    ? [
                        {
                          key: "relationType",
                          label: copy.relationType,
                          value: props.detail.mediaContext.relationTypeLabel,
                        },
                      ]
                    : []),
                  ...(props.detail.mediaContext.artifactRoleLabel
                    ? [
                        {
                          key: "artifactRole",
                          label: copy.artifactRole,
                          value: props.detail.mediaContext.artifactRoleLabel,
                        },
                      ]
                    : []),
                  ...(props.detail.mediaContext.previewAvailabilityLabel
                    ? [
                        {
                          key: "previewAvailability",
                          label: copy.previewAvailability,
                          value: props.detail.mediaContext.previewAvailabilityLabel,
                        },
                      ]
                    : []),
                  ...(props.detail.mediaContext.downloadAvailabilityLabel
                    ? [
                        {
                          key: "downloadAvailability",
                          label: copy.downloadAvailability,
                          value: props.detail.mediaContext.downloadAvailabilityLabel,
                        },
                      ]
                    : []),
                  ...(props.detail.mediaContext.fallbackReasonLabel &&
                  props.detail.mediaContext.fallbackReasonLabel !== "-"
                    ? [
                        {
                          key: "fallbackReason",
                          label: copy.fallbackReason,
                          value: props.detail.mediaContext.fallbackReasonLabel,
                        },
                      ]
                    : []),
                  ...(props.detail.mediaContext.binaryReviewPathLabel
                    ? [
                        {
                          key: "binaryReviewPath",
                          label: copy.binaryReviewPath,
                          value: props.detail.mediaContext.binaryReviewPathLabel,
                        },
                      ]
                    : []),
                  {
                    key: "mediaSummary",
                    label: copy.requestSummary,
                    value: props.detail.mediaContext.summary,
                  },
                  {
                    key: "mediaWorkspace",
                    label: copy.drilldown,
                    value: renderLinkOrFallback(
                      props.detail.mediaContext.workspaceLinkLabel,
                      props.detail.mediaContext.workspaceHref,
                    ),
                  },
                ],
              },
            ]
          : []),
        ...(props.detail.sameDeviceLogContext
          ? [
              {
                title: copy.sameDeviceLogContext,
                items: [
                  {
                    key: "sameDeviceHeadline",
                    label: copy.headline,
                    value: props.detail.sameDeviceLogContext.headline,
                  },
                  {
                    key: "sameDeviceArtifactIdentity",
                    label: copy.artifactIdentity,
                    value: props.detail.sameDeviceLogContext.artifactIdentityLabel,
                  },
                  {
                    key: "sameDeviceCaptureTrigger",
                    label: copy.captureTrigger,
                    value: props.detail.sameDeviceLogContext.captureTriggerLabel,
                  },
                  {
                    key: "sameDevicePreviewReadiness",
                    label: copy.previewReadiness,
                    value: props.detail.sameDeviceLogContext.previewReadinessLabel,
                  },
                  {
                    key: "sameDeviceDownloadReadiness",
                    label: copy.downloadReadiness,
                    value: props.detail.sameDeviceLogContext.downloadReadinessLabel,
                  },
                  {
                    key: "sameDeviceBundleHashPresence",
                    label: copy.bundleHashPresence,
                    value: props.detail.sameDeviceLogContext.bundleHashPresenceLabel,
                  },
                  {
                    key: "sameDeviceManifestVersion",
                    label: copy.manifestVersion,
                    value: props.detail.sameDeviceLogContext.manifestVersionLabel,
                  },
                  {
                    key: "sameDeviceDeliveryPath",
                    label: copy.deliveryPath,
                    value: props.detail.sameDeviceLogContext.deliveryPathStateLabel,
                  },
                  {
                    key: "sameDevicePreviewHeadline",
                    label: copy.previewHeadline,
                    value: props.detail.sameDeviceLogContext.previewHeadlineLabel,
                  },
                  {
                    key: "sameDeviceLatestTrace",
                    label: copy.latestTrace,
                    value: formatLatestTraceValue(
                      props.detail.sameDeviceLogContext.latestRequestIdLabel,
                      props.detail.sameDeviceLogContext.latestCorrelationIdLabel,
                    ),
                  },
                  {
                    key: "sameDeviceSummary",
                    label: copy.requestSummary,
                    value: props.detail.sameDeviceLogContext.summary,
                  },
                  {
                    key: "sameDeviceWorkspace",
                    label: copy.drilldown,
                    value: renderLinkOrFallback(
                      props.detail.sameDeviceLogContext.workspaceLinkLabel,
                      props.detail.sameDeviceLogContext.workspaceHref,
                    ),
                  },
                  ...(props.detail.sameDeviceLogContext.requestTraceHref &&
                  props.detail.sameDeviceLogContext.requestTraceLinkLabel
                    ? [
                        {
                          key: "sameDeviceRequestTrace",
                          label: copy.traceLink,
                          value: renderLinkOrFallback(
                            props.detail.sameDeviceLogContext.requestTraceLinkLabel,
                            props.detail.sameDeviceLogContext.requestTraceHref,
                          ),
                        },
                      ]
                    : []),
                ],
              },
            ]
          : []),
        {
          title: copy.flow,
          items: [
            {
              key: "createdAt",
              label: copy.created,
              value: props.detail.createdAtLabel,
            },
            {
              key: "status",
              label: copy.status,
              value: props.detail.statusLabel,
            },
            {
              key: "errorCode",
              label: copy.error,
              value: props.detail.errorCode,
            },
            {
              key: "statusCode",
              label: copy.httpStatus,
              value: props.detail.statusCodeLabel,
            },
            {
              key: "signalKey",
              label: copy.signal,
              value: props.detail.signalKey,
            },
            {
              key: "stage",
              label: copy.stage,
              value: props.detail.stage,
            },
            {
              key: "platform",
              label: copy.platform,
              value: props.detail.platformLabel,
            },
            {
              key: "runtimeSummary",
              label: copy.runtimeSummary,
              value: props.detail.runtimeSummaryLabel,
            },
            {
              key: "runtimeAppBuildNumber",
              label: copy.runtimeAppBuildNumber,
              value: props.detail.runtimeAppBuildNumberLabel,
            },
            {
              key: "runtimeLocale",
              label: copy.runtimeLocale,
              value: props.detail.runtimeLocaleLabel,
            },
            {
              key: "runtimeTimezone",
              label: copy.runtimeTimezone,
              value: props.detail.runtimeTimezoneOffsetBucketLabel,
            },
            {
              key: "runtimeTimezoneRegion",
              label: copy.runtimeTimezoneRegion,
              value: props.detail.runtimeTimezoneNameBucketLabel,
            },
            {
              key: "runtimeDeviceModel",
              label: copy.runtimeDeviceModel,
              value: props.detail.runtimeDeviceModelBucketLabel,
            },
            {
              key: "runtimeDeviceModelName",
              label: copy.runtimeDeviceModelName,
              value: props.detail.runtimeDeviceModelNameBucketLabel,
            },
            {
              key: "runtimeOsMajor",
              label: copy.runtimeOsMajor,
              value: props.detail.runtimeOsMajorBucketLabel,
            },
            {
              key: "runtimeOsVersionMinor",
              label: copy.runtimeOsVersionMinor,
              value: props.detail.runtimeOsVersionMinorBucketLabel,
            },
            {
              key: "runtimeNetworkType",
              label: copy.runtimeNetworkType,
              value: props.detail.runtimeNetworkTypeBucketLabel,
            },
            {
              key: "runtimePhysicalDevice",
              label: copy.runtimePhysicalDevice,
              value: props.detail.runtimePhysicalDeviceBucketLabel,
            },
            {
              key: "providerKind",
              label: copy.providerKind,
              value: props.detail.providerKindLabel,
            },
            {
              key: "reauthMethod",
              label: copy.reauthMethod,
              value: props.detail.reauthMethodBucketLabel,
            },
            {
              key: "recentAuthAge",
              label: copy.recentAuthAge,
              value: props.detail.recentAuthAgeBucketLabel,
            },
            {
              key: "saveBeforeExit",
              label: copy.saveBeforeExit,
              value: props.detail.requestSaveBeforeExitLabel,
            },
            {
              key: "suggestionUsed",
              label: copy.suggestionUsed,
              value: props.detail.requestSuggestionUsedLabel,
            },
            {
              key: "profileCompletionStep",
              label: copy.profileCompletionStep,
              value: props.detail.requestProfileCompletionStepLabel,
            },
            {
              key: "attemptCountBucket",
              label: copy.attemptCountBucket,
              value: props.detail.requestAttemptCountBucketLabel,
            },
            {
              key: "photoConfirmLatency",
              label: copy.photoConfirmLatency,
              value: props.detail.requestPhotoConfirmLatencyBucketLabel,
            },
            {
              key: "requestDraftSource",
              label: copy.requestDraftSource,
              value: props.detail.requestDraftSourceBucketLabel,
            },
            {
              key: "requestMediaAttachCount",
              label: copy.requestMediaAttachCount,
              value: props.detail.requestMediaAttachCountBucketLabel,
            },
            {
              key: "requestSpecies",
              label: copy.requestSpecies,
              value: props.detail.requestSpeciesBucketLabel,
            },
            {
              key: "requestBreedSelection",
              label: copy.requestBreedSelection,
              value: props.detail.requestBreedSelectionBucketLabel,
            },
            {
              key: "requestTargetUserLookupKind",
              label: copy.requestTargetUserLookupKind,
              value: props.detail.requestTargetUserLookupKindLabel,
            },
            {
              key: "requestPublicPetCount",
              label: copy.requestPublicPetCount,
              value: props.detail.requestPublicPetCountBucketLabel,
            },
            {
              key: "requestProfileMutationFieldSet",
              label: copy.requestProfileMutationFieldSet,
              value: props.detail.requestProfileMutationFieldSetBucketLabel,
            },
            {
              key: "requestRoomKind",
              label: copy.requestRoomKind,
              value: props.detail.requestRoomKindLabel,
            },
            {
              key: "requestMessageLength",
              label: copy.requestMessageLength,
              value: props.detail.requestMessageLengthBucketLabel,
            },
            {
              key: "requestMembershipCount",
              label: copy.requestMembershipCount,
              value: props.detail.requestMembershipCountBucketLabel,
            },
            {
              key: "requestScheduleRecurrence",
              label: copy.requestScheduleRecurrence,
              value: props.detail.requestScheduleRecurrenceBucketLabel,
            },
            {
              key: "requestDosePresence",
              label: copy.requestDosePresence,
              value: props.detail.requestDosePresenceLabel,
            },
            {
              key: "requestOccurrenceActionKind",
              label: copy.requestOccurrenceActionKind,
              value: props.detail.requestOccurrenceActionKindLabel,
            },
            {
              key: "requestSnoozeWindow",
              label: copy.requestSnoozeWindow,
              value: props.detail.requestSnoozeWindowBucketLabel,
            },
            {
              key: "requestRideVisitLeadTime",
              label: copy.requestRideVisitLeadTime,
              value: props.detail.requestRideVisitLeadTimeBucketLabel,
            },
            {
              key: "requestRideLongestAddressLength",
              label: copy.requestRideLongestAddressLength,
              value: props.detail.requestRideLongestAddressLengthBucketLabel,
            },
            {
              key: "requestRideMemoProvided",
              label: copy.requestRideMemoProvided,
              value: props.detail.requestRideMemoProvidedLabel,
            },
            {
              key: "requestCommunityUploadSource",
              label: copy.requestCommunityUploadSource,
              value: props.detail.requestCommunityUploadSourceBucketLabel,
            },
            {
              key: "requestCommunityContentLength",
              label: copy.requestCommunityContentLength,
              value: props.detail.requestCommunityContentLengthBucketLabel,
            },
            {
              key: "requestCommunityCommentLength",
              label: copy.requestCommunityCommentLength,
              value: props.detail.requestCommunityCommentLengthBucketLabel,
            },
            {
              key: "requestCommunityImageCount",
              label: copy.requestCommunityImageCount,
              value: props.detail.requestCommunityImageCountBucketLabel,
            },
            {
              key: "requestCommunityPetContextAttached",
              label: copy.requestCommunityPetContextAttached,
              value: props.detail.requestCommunityPetContextAttachedLabel,
            },
            {
              key: "requestCommunityTopicTag",
              label: copy.requestCommunityTopicTag,
              value: props.detail.requestCommunityTopicTagLabel,
            },
            {
              key: "requestCommunityRetryCount",
              label: copy.requestCommunityRetryCount,
              value: props.detail.requestCommunityRetryCountBucketLabel,
            },
            {
              key: "requestReceiptExtractSource",
              label: copy.requestReceiptExtractSource,
              value: props.detail.requestReceiptExtractSourceBucketLabel,
            },
            {
              key: "requestReceiptImageByteSize",
              label: copy.requestReceiptImageByteSize,
              value: props.detail.requestReceiptImageByteSizeBucketLabel,
            },
            {
              key: "requestReceiptExtractionConfidence",
              label: copy.requestReceiptExtractionConfidence,
              value: props.detail.requestReceiptExtractionConfidenceBucketLabel,
            },
            {
              key: "requestReceiptManualOverride",
              label: copy.requestReceiptManualOverride,
              value: props.detail.requestReceiptManualOverrideLabel,
            },
            {
              key: "requestReceiptCategoryMutationType",
              label: copy.requestReceiptCategoryMutationType,
              value: props.detail.requestReceiptCategoryMutationTypeLabel,
            },
            {
              key: "requestHealthFileSize",
              label: copy.requestHealthFileSize,
              value: props.detail.requestHealthFileSizeBucketLabel,
            },
            {
              key: "requestHealthFileType",
              label: copy.requestHealthFileType,
              value: props.detail.requestHealthFileTypeBucketLabel,
            },
            {
              key: "requestHealthAnalysisFallbackPath",
              label: copy.requestHealthAnalysisFallbackPath,
              value: props.detail.requestHealthAnalysisFallbackPathLabel,
            },
            {
              key: "requestHealthConfirmToSaveLatency",
              label: copy.requestHealthConfirmToSaveLatency,
              value: props.detail.requestHealthConfirmToSaveLatencyBucketLabel,
            },
            {
              key: "release",
              label: copy.release,
              value: props.detail.release ?? "-",
            },
            {
              key: "appVersion",
              label: copy.app,
              value: props.detail.appVersion ?? "-",
            },
            {
              key: "deviceInstallId",
              label: copy.device,
              value: props.detail.deviceInstallId ?? "-",
            },
          ],
        },
        {
          title: correlationLabel,
          items: [
            {
              key: "requestId",
              label: requestLabel,
              value: renderLinkOrFallback(props.detail.requestId, props.detail.drilldownTarget.request),
            },
            {
              key: "correlationId",
              label: correlationLabel,
              value: renderLinkOrFallback(
                props.detail.correlationId,
                props.detail.drilldownTarget.correlation,
              ),
            },
            {
              key: "jobId",
              label: copy.job,
              value: renderLinkOrFallback(props.detail.jobLabel, props.detail.drilldownTarget.jobs),
            },
            {
              key: "userIdHash",
              label: copy.user,
              value: renderLinkOrFallback(
                props.detail.userIdHash,
                props.detail.drilldownTarget.userSummary,
              ),
            },
            {
              key: "userCue",
              label: copy.userCue,
              value: props.detail.accountIdentityLabel,
            },
            {
              key: "providerCue",
              label: copy.providerCue,
              value: props.detail.accountProviderIdentityLabel,
            },
            {
              key: "linkedProviders",
              label: copy.linkedProviders,
              value: props.detail.accountLinkedProviderCountLabel,
            },
            {
              key: "petIdHash",
              label: copy.pet,
              value: props.detail.petIdHash ?? "-",
            },
            {
              key: "recordId",
              label: copy.record,
              value: props.detail.recordId ?? "-",
            },
            {
              key: "receiptId",
              label: copy.receipt,
              value: props.detail.receiptId ?? "-",
            },
            {
              key: "fileId",
              label: copy.file,
              value: props.detail.fileId ?? "-",
            },
          ],
        },
        {
          title: copy.lastEvent,
          items: [
            {
              key: "lastEventName",
              label: copy.event,
              value: props.detail.lastEventNameLabel,
            },
            {
              key: "lastEventAt",
              label: copy.occurred,
              value: props.detail.lastEventAtLabel,
            },
          ],
        },
        {
          title: copy.drilldown,
          items: [
            {
              key: "deviceErrors",
              label: copy.deviceErrors,
              value: renderLinkOrFallback(
                props.detail.deviceInstallIdLabel,
                props.detail.drilldownTarget.deviceErrors,
              ),
            },
            {
              key: "deviceKpis",
              label: copy.deviceKpis,
              value: renderLinkOrFallback(
                props.detail.deviceInstallIdLabel,
                props.detail.drilldownTarget.deviceKpis,
              ),
            },
            {
              key: "releaseRegression",
              label: copy.release,
              value: renderLinkOrFallback(
                props.detail.release ?? props.detail.appVersion,
                props.detail.drilldownTarget.releaseRegression,
              ),
            },
            {
              key: "similarFailures",
              label: copy.similarFailures,
              value: renderExternalLinkOrFallback(
                copy.openSimilar,
                props.detail.observability.similarFailuresUrl,
              ),
            },
          ],
        },
        {
          title: copy.freshness,
          items: [
            {
              key: "sourceStatus",
              label: copy.freshness,
              value:
                props.sourceFreshness.length > 0 ? (
                  <AdminSourceStatusGrid
                    items={props.sourceFreshness}
                    statusLabel={copy.freshness}
                  />
                ) : (
                  "-"
                ),
            },
          ],
        },
      ]}
    />
  );
}
